































































/* eslint-disable @typescript-eslint/camelcase */
import Vue from 'vue'
import { Paginated } from '@feathersjs/feathers'
import Button from 'primevue/button'
import ConfirmDialog from 'primevue/confirmdialog'

import { lckServices } from '@/services/lck-api'
import { LckProcess, LckProcessRun, LckTable, LckTableColumn, PROCESS_TRIGGER } from '@/services/lck-api/definitions'

import Process from '@/components/store/Process/Process.vue'
import { ROUTES_NAMES } from '@/router/paths'

export default Vue.extend({
  name: 'ProcessListing',
  components: {
    'confirm-dialog': ConfirmDialog,
    'p-button': Vue.extend(Button),
    'lck-process': Process,
  },
  props: {
    workspaceId: {
      type: String,
      required: true,
    },
    processId: {
      type: String,
      required: false,
    },
    sidebarActive: {
      type: Boolean,
      default: true,
    },
  },
  data () {
    return {
      loading: false,
      submitting: false,
      routeNameProcessDetail: ROUTES_NAMES.WORKSPACE_ADMIN.PROCESS_DETAIL,
      processResult: [] as LckProcess[],
      currentProcess: null as LckProcess | null,
      suggestionsTable: [] as { text: string; value: string }[],
      suggestionsColumn: [] as { text: string; value: string }[],
    }
  },
  methods: {
    async onInputProcess (data: Partial<LckProcess>) {
      this.submitting = true
      try {
        if (data.id) {
          /**
           * Id exist, we only patch
           */
          const processPatched = await lckServices.process.patch(data.id, {
            text: data.text,
            url: data.url || '',
            enabled: data.enabled,
            table_id: data.table_id,
            trigger: data.trigger,
            maximumNumberSuccess: data.maximumNumberSuccess,
            settings: {
              column_id: data.settings?.column_id,
            },
          })
          /**
           * Update our current data
           */
          const actualProcessIndex = this.processResult.findIndex(p => p.id === data.id)
          if (actualProcessIndex > -1) {
            this.$set(
              this.processResult,
              actualProcessIndex,
              {
                ...this.processResult[actualProcessIndex],
                ...processPatched,
                table: data.table,
                settings: {
                  ...processPatched,
                  column: data.settings?.column,
                },
              },
            )
          }
        } else {
          /**
           * This is a creation.
           * We have to create the process
           * AND the trigger if we are on a table.
           * If we don't create the trigger,
           * the process won't be visible for the user.
           */
          const process = await lckServices.process.create({
            text: data.text,
            url: data.url || '',
            enabled: data.enabled,
            table_id: data.table_id,
            workspace_id: this.workspaceId,
            trigger: data.trigger,
            maximumNumberSuccess: data.maximumNumberSuccess,
            settings: {
              column_id: data.settings?.column_id,
            },
          })
          this.currentProcess = process
          this.currentProcess.table = data.table
          this.currentProcess.settings = {
            ...this.currentProcess.settings,
            column: data.settings?.column,
          }
          /**
           * Finally, add the currentProcess to the list of processes
           */
          this.processResult.push(this.currentProcess)
        }
        // Go back to display processes list
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('error.http.' + error.code),
          detail: this.$t('error.lck.' + error.data.code),
          life: 5000,
        })
      }
      this.submitting = false
    },
    async onDeleteProcess () {
      if (this.currentProcess) {
        this.$confirm.require({
          message: `${this.$t('form.specificDeleteConfirmation')} ${this.currentProcess?.text}`,
          header: this.$t('form.confirmation'),
          icon: 'pi pi-exclamation-triangle',
          accept: async () => {
            try {
              await lckServices.process.remove(this.currentProcess?.id as string)
              this.processResult = this.processResult.filter(p => p.id !== this.currentProcess?.id)
              this.currentProcess = null
              this.$router.push({
                name: ROUTES_NAMES.WORKSPACE_ADMIN.PROCESS,
                params: {
                  workspaceId: this.workspaceId,
                },
              })
            } catch (error) {
              this.$toast.add({
                severity: 'error',
                summary: this.$t('error.basic'),
                detail: this.$t('error.http.' + error.code),
                life: 5000,
              })
            }
          },
        })
      }
    },
    async loadProcesses () {
      this.loading = true
      try {
        const processByWorkspace = await lckServices.process.find({
          query: {
            workspace_id: this.workspaceId,
            $limit: 50,
            $eager: 'table',
            $sort: {
              createdAt: 1,
            },
          },
        }) as Paginated<LckProcess>
        this.processResult = processByWorkspace.data
      } catch (error) {
        console.error(error)
      }
      this.loading = false
    },
    onClickCreateProcess () {
      this.$router.push({
        name: ROUTES_NAMES.WORKSPACE_ADMIN.PROCESS_ADD,
        params: {
          workspaceId: this.workspaceId,
        },
      })
      this.currentProcess = new LckProcess()
    },
    async onSearchTable ({ query }: { query: string }) {
      const tableResult = await lckServices.table.find({
        query: {
          'database.workspace_id': this.workspaceId,
          $joinRelation: 'database',
          'table.text': {
            $ilike: `%${query}%`,
          },
        },
      }) as Paginated<LckTable>
      this.suggestionsTable = tableResult.data.map(tr => ({
        text: tr.text,
        value: tr.id,
      }))
    },
    async onSearchColumn ({ query, tableId }: { query: string; tableId: string }) {
      const columnResult = await lckServices.tableColumn.find({
        query: {
          table_id: tableId,
          text: {
            $ilike: `%${query}%`,
          },
        },
      }) as Paginated<LckTableColumn>
      this.suggestionsColumn = columnResult.data.map(tr => ({
        text: tr.text,
        value: tr.id,
      }))
    },
    async onRefreshRuns (process: LckProcess) {
      const processRunResult = await lckServices.processRun.find({
        query: {
          process_id: process.id,
          $limit: 50,
          $sort: {
            createdAt: -1,
          },
        },
      }) as Paginated<LckProcessRun>
      this.$set(process, 'runs', processRunResult.data)
    },
    async loadProcess (processId: string) {
      if (processId === 'add') return
      /**
       * Load the column if the process is with settings
       */
      this.currentProcess = await lckServices.process.get(processId, {
        query: {
          $eager: 'table',
        },
      })
      if (
        this.currentProcess.trigger === PROCESS_TRIGGER.UPDATE_ROW_DATA &&
        this.currentProcess.settings?.column_id
      ) {
        this.$set(
          this.currentProcess.settings,
          'column',
          await lckServices.tableColumn.get(this.currentProcess.settings.column_id),
        )
      }
      this.onRefreshRuns(this.currentProcess)
    },
    resetCurrentProcess () {
      this.currentProcess = null
    },
    async triggerRun () {
      if (!this.currentProcess) return
      const run = await lckServices.processRun.create({
        process_id: this.currentProcess.id,
      }) as LckProcessRun
      this.$set(this.currentProcess, 'runs', [run, ...(this.currentProcess.runs || [])])
    },
  },
  mounted () {
    this.loadProcesses()
  },
  watch: {
    workspaceId () {
      this.loadProcesses()
    },
    processId: {
      handler (newValue) {
        if (!newValue) return
        this.loadProcess(this.processId)
      },
      immediate: true,
    },
  },
})
